(() => {
  'use strict';

  const chatSelector = 'shopify-chat';
  const positionKey = 'mti-chat-button-position';
  let dragState = null;

  const clamp = (value, minimum, maximum) => Math.min(Math.max(value, minimum), maximum);

  const findActivator = (root) => {
    const button = root?.querySelector('[part~="activator"]');
    if (button) return button;

    for (const element of root?.querySelectorAll('*') || []) {
      if (element.shadowRoot) {
        const nestedButton = findActivator(element.shadowRoot);
        if (nestedButton) return nestedButton;
      }
    }

    return null;
  };

  const getActivator = (event) => event.composedPath().find((node) => {
    return node && typeof node.getAttribute === 'function' && (node.getAttribute('part') || '').split(/\s+/).includes('activator');
  });

  const getSavedPosition = () => {
    try {
      const position = JSON.parse(localStorage.getItem(positionKey) || '{}');
      return {
        x: Number.isFinite(position.x) ? position.x : 0,
        y: Number.isFinite(position.y) ? position.y : 0
      };
    } catch {
      return { x: 0, y: 0 };
    }
  };

  const applyPosition = (button, x, y) => {
    button.dataset.mtiChatDragX = String(x);
    button.dataset.mtiChatDragY = String(y);
    button.style.transform = `translate3d(${x}px, ${y}px, 0)`;
  };

  const restorePosition = (chat) => {
    const button = findActivator(chat.shadowRoot);
    if (!button || button.dataset.mtiChatDragRestored === 'true') return false;

    const saved = getSavedPosition();
    const rect = button.getBoundingClientRect();
    const x = clamp(saved.x, -rect.left, window.innerWidth - rect.width - rect.left);
    const y = clamp(saved.y, -rect.top, window.innerHeight - rect.height - rect.top);
    applyPosition(button, x, y);
    button.dataset.mtiChatDragRestored = 'true';
    return true;
  };

  const beginDrag = (event) => {
    if (event.button !== 0 || !event.isPrimary) return;

    const button = getActivator(event);
    const chat = event.composedPath().find((node) => node.tagName === chatSelector.toUpperCase());
    if (!chat || !button) return;

    const rect = chat.getBoundingClientRect();
    const buttonRect = button.getBoundingClientRect();
    dragState = {
      chat,
      button,
      pointerId: event.pointerId,
      startX: event.clientX,
      startY: event.clientY,
      startLeft: buttonRect.left,
      startTop: buttonRect.top,
      width: buttonRect.width,
      height: buttonRect.height,
      offsetX: Number(button.dataset.mtiChatDragX || 0),
      offsetY: Number(button.dataset.mtiChatDragY || 0),
      moved: false
    };

    chat.classList.add('mti-chat-dragging');
    button.style.cursor = 'grabbing';
  };

  const moveDrag = (event) => {
    if (!dragState || event.pointerId !== dragState.pointerId) return;

    const deltaX = event.clientX - dragState.startX;
    const deltaY = event.clientY - dragState.startY;
    if (!dragState.moved && Math.hypot(deltaX, deltaY) < 5) return;

    dragState.moved = true;
    const baseLeft = dragState.startLeft - dragState.offsetX;
    const baseTop = dragState.startTop - dragState.offsetY;
    const x = clamp(dragState.offsetX + deltaX, -baseLeft, window.innerWidth - dragState.width - baseLeft);
    const y = clamp(dragState.offsetY + deltaY, -baseTop, window.innerHeight - dragState.height - baseTop);

    applyPosition(dragState.button, x, y);
    event.preventDefault();
  };

  const endDrag = (event) => {
    if (!dragState || event.pointerId !== dragState.pointerId) return;

    const { chat, button, moved } = dragState;
    chat.classList.remove('mti-chat-dragging');
    button.style.cursor = '';

    if (moved) {
      try {
        localStorage.setItem(positionKey, JSON.stringify({
          x: Number(button.dataset.mtiChatDragX),
          y: Number(button.dataset.mtiChatDragY)
        }));
      } catch {}

      button.addEventListener('click', (clickEvent) => {
        clickEvent.preventDefault();
        clickEvent.stopImmediatePropagation();
      }, { capture: true, once: true });
    }

    dragState = null;
  };

  const attachDragHandlers = (chat) => {
    if (chat.dataset.mtiChatDragAttached === 'true') return;

    restorePosition(chat);
    chat.addEventListener('pointerdown', beginDrag);
    window.addEventListener('pointermove', moveDrag, { passive: false });
    window.addEventListener('pointerup', endDrag, { passive: false });
    window.addEventListener('pointercancel', endDrag, { passive: false });
    chat.dataset.mtiChatDragAttached = 'true';

    const watchedRoots = new WeakSet();
    const restoreObserver = new MutationObserver(() => {
      observeShadowRoots(chat.shadowRoot);
      restorePosition(chat);
    });
    const observeShadowRoots = (root) => {
      if (!root || watchedRoots.has(root)) return;
      watchedRoots.add(root);
      restoreObserver.observe(root, { childList: true, subtree: true });
      root.querySelectorAll('*').forEach((element) => {
        if (element.shadowRoot) observeShadowRoots(element.shadowRoot);
      });
    };

    observeShadowRoots(chat.shadowRoot);
    restorePosition(chat);
  };

  const observeChats = () => {
    document.querySelectorAll(chatSelector).forEach(attachDragHandlers);

    if (typeof MutationObserver === 'undefined') return;

    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        mutation.addedNodes.forEach((node) => {
          if (node.nodeType === Node.ELEMENT_NODE && node.matches(chatSelector)) {
            attachDragHandlers(node);
          }
        });
      });
    });

    observer.observe(document.documentElement, { childList: true, subtree: true });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', observeChats, { once: true });
  } else {
    observeChats();
  }
})();
